import type { SessionMetricPoint } from "api/types/charts";
import type { DistributionDatum } from "../Targets/TargetDistributionChart";
import type { RecordMetricField } from "../Targets/metricChartRegistry";
import { PROMPT_LEVELS } from "../Targets/TargetBuilderPanel/TargetBuilderPanel.types";

// ---------------------------------------------------------------------------
// Prompt-level distribution derivation
//
// The `prompt_level_distribution` / `latency_by_prompt_level` fields are a
// per-session `Record<promptCode, number>`. The distribution bar chart shows one
// bar per prompt level combined across the whole target: counts are summed
// (total trials at each level), latencies are averaged (mean response time at
// each level). Bars follow the canonical PROMPT_LEVELS order; levels with no
// data across any session are dropped.
// ---------------------------------------------------------------------------

/** promptCode → human label, from the canonical prompt hierarchy. */
const CODE_TO_LABEL: Record<string, string> = Object.fromEntries(
  PROMPT_LEVELS.map((p) => [p.code, p.label])
);

export interface DerivePromptDistributionOptions {
  /** The record field to read per session. */
  field: RecordMetricField;
  /** How to combine each level's per-session values across the target. */
  aggregate: "sum" | "mean";
  /** Optional per-value transform applied to each session value (e.g. ms→s). */
  transform?: (value: number) => number;
}

/**
 * Aggregates a target's per-session prompt-level records into distribution bars.
 *
 * @param metrics - Session metric points (oldest first).
 * @param opts - Field, aggregation, and optional value transform.
 * @returns One `{ label, value }` per prompt level that has data, in canonical
 *          prompt-hierarchy order. `sum` values are whole totals; `mean` values
 *          are averaged over the sessions that recorded the level.
 */
export function derivePromptDistribution(
  metrics: SessionMetricPoint[],
  { field, aggregate, transform }: DerivePromptDistributionOptions
): DistributionDatum[] {
  // Collect every value seen per code across sessions, in encounter order.
  const byCode = new Map<string, number[]>();
  for (const m of metrics) {
    const record = m[field];
    if (!record) continue;
    for (const [code, raw] of Object.entries(record)) {
      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
      const value = transform ? transform(raw) : raw;
      if (!Number.isFinite(value)) continue;
      const bucket = byCode.get(code);
      if (bucket) bucket.push(value);
      else byCode.set(code, [value]);
    }
  }

  // Emit in canonical prompt order, plus any unknown codes after, dropping empties.
  const orderedCodes = [
    ...PROMPT_LEVELS.map((p) => p.code).filter((c) => byCode.has(c)),
    ...[...byCode.keys()].filter((c) => !Object.hasOwn(CODE_TO_LABEL, c)),
  ];

  return orderedCodes.map((code) => {
    const values = byCode.get(code) ?? [];
    const total = values.reduce((sum, v) => sum + v, 0);
    const value = aggregate === "mean" ? total / values.length : total;
    return { label: CODE_TO_LABEL[code] ?? code, value };
  });
}
